/**
 * The airship's shader additions (ADR 0033), lifted from brad-airship as
 * `ShaderHook`s (the slice 2a rule: only `material-hooks.ts` edits three.js's
 * chunks; the prototype patched them with `.replace()` in each module):
 *
 * - `planarHook`: world-space planar mapping by the dominant normal axis
 *   (`airship-surfaces.js` `installPlanarTexture`): tops take XZ, sides XY or
 *   ZY; one texture fetch, a near-white modulation of the material's colour.
 * - `grimeHook`: the hull's weathering map in hull space (`installGrime`).
 * - `windHook`: GPU wind and flight motion by mode (`airship-wind.js`); the
 *   vertex attribute `aWind` comes from `windAttribute`.
 * - `revealHook`: the Octopath roof reveal (`airship-reveal.js`): an ordered
 *   dither discards the room's roof while the traveler is inside it.
 * - `radianceHook`: the static irradiance volume (the shared engine's
 *   `radiance.js` `installRadiance`, its default slots): daylight visibility in
 *   alpha, baked bounce in RGB; no point lights.
 * - `lampHook`: the baked lamp light over the deck (`airship-lamps.js`),
 *   scaled by the time of day's `lamps`.
 * - `cloudShadowHook`: drifting cloud shadows on the deck's direct light
 *   (`airship-weather.js` `installCloudShadow`).
 * - `crtHook`: the bridge console's vertex-coloured phosphor glow.
 *
 * The uniforms objects are shared by reference: one write moves every
 * material that carries the hook (the prototype's shared uniforms).
 */
import * as THREE from 'three';
import type { ShaderHook } from '../material-hooks.ts';

/** The hull grime map's frame in hull space (world x along the ship, y up; `airship-surfaces.js` `GRIME_FRAME`). */
export const grimeFrame = { x0: 27, x1: 64, y0: -7, y1: 3 } as const;

export const planarHook = (texture: THREE.Texture, scale: number): ShaderHook => ({
  key: 'ship-planar',
  uniforms: { planarMap: { value: texture }, planarScale: { value: scale } },
  vertexHeader: 'varying vec3 plPos,plNormal;',
  fragmentHeader: 'uniform sampler2D planarMap;uniform float planarScale;varying vec3 plPos,plNormal;',
  vertex: [{ chunk: 'worldpos_vertex', after: 'plPos=(modelMatrix*vec4(transformed,1.)).xyz;plNormal=mat3(modelMatrix)*objectNormal;' }],
  fragment: [
    {
      chunk: 'map_fragment',
      after: `vec3 plN=abs(plNormal);
vec2 plUv=plN.y>max(plN.x,plN.z)?plPos.xz:(plN.z>plN.x?plPos.xy:plPos.zy);
diffuseColor.rgb*=texture2D(planarMap,plUv*planarScale).rgb*1.12;`,
    },
  ],
});

export const grimeHook = (texture: THREE.Texture): ShaderHook => {
  const f = grimeFrame;
  return {
    key: 'ship-grime',
    uniforms: { grimeMap: { value: texture } },
    vertexHeader: 'varying vec3 grWorld;',
    fragmentHeader: 'uniform sampler2D grimeMap;varying vec3 grWorld;',
    vertex: [{ chunk: 'worldpos_vertex', after: 'grWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
    fragment: [{ chunk: 'map_fragment', after: `diffuseColor.rgb*=texture2D(grimeMap,vec2((grWorld.x-(${f.x0.toFixed(1)}))/${(f.x1 - f.x0).toFixed(1)},(grWorld.y-(${f.y0.toFixed(1)}))/${(f.y1 - f.y0).toFixed(1)})).rgb;` }],
  };
};

/** The wind's shared uniforms (`airship-wind.js` `windUniforms`): the CPU advances a few scalars a frame. */
export type WindUniforms = {
  readonly windTime: THREE.IUniform<number>;
  readonly windStrength: THREE.IUniform<number>;
  readonly windGust: THREE.IUniform<number>;
  readonly propAngle: THREE.IUniform<number>;
  readonly sailSet: THREE.IUniform<number>;
};
export const createWindUniforms = (): WindUniforms => ({ windTime: { value: 0 }, windStrength: { value: 0.6 }, windGust: { value: 0 }, propAngle: { value: 0 }, sailSet: { value: 0 } });

/**
 * The wind's modes. Every animated vertex carries an `aWind` vec4 whose meaning depends on the mode:
 * rope (x the sway weight: 0 at both fixed ends, 1 mid-span), sail (x the billow weight, y a phase, z 1 for a
 * square sail bellying forward), flag (x the distance from the hoist, y the flag's length), cloth (x the drop below
 * the line, y a phase), prop (xy the hub on the shaft axis parallel to x, z the spin's sign), lantern (x the drop
 * below the hook, y a phase).
 */
export type WindMode = 'rope' | 'sail' | 'flag' | 'cloth' | 'prop' | 'lantern';

const windBodies: Readonly<Record<WindMode, string>> = {
  rope: `
    float sw=aWind.x*(.035+.05*windStrength);
    transformed+=vec3(.15,.25,1.)*sw*sin(windTime*1.3+position.x*.21+position.y*.13)
      +vec3(0.,0.,1.)*sw*.5*sin(windTime*2.9+position.x*.5);`,
  sail: `
    float gust=.75+.25*sin(windTime*.7+aWind.y)+windGust*.35;
    float belly=aWind.x*(.25+.9*windStrength*sailSet)*gust;
    float ripple=aWind.x*aWind.x*.06*(.3+windStrength)*sin(windTime*3.1+position.x*1.3-position.y*.9+aWind.y);
    vec3 bellyDir=aWind.z>.5?vec3(1.,0.,.12):vec3(.18,0.,1.);
    transformed+=bellyDir*(belly+ripple);`,
  flag: `
    float d=aWind.x,len=max(aWind.y,.01),k=d/len;
    float amp=(.08+.26*windStrength)*k;
    float wave=sin(d*2.4-windTime*(5.5+4.*windStrength)+position.y*.8);
    transformed.z+=amp*wave;
    transformed.y+=amp*.35*cos(d*2.4-windTime*(5.5+4.*windStrength))-k*k*.12*(1.-windStrength);
    transformed.x-=k*.08*abs(wave);`,
  cloth: `
    float drop=aWind.x,sway=(.05+.16*windStrength)*drop;
    transformed.z+=sway*(.7+.3*sin(windTime*2.3+aWind.y))+sway*.35*sin(windTime*5.1+position.x*3.+drop*4.);
    transformed.x-=sway*.3*(1.+windGust);`,
  prop: `
    vec2 rel=transformed.yz-aWind.xy;float a=propAngle*aWind.z;
    transformed.yz=aWind.xy+mat2(cos(a),sin(a),-sin(a),cos(a))*rel;`,
  lantern: `
    float swing=aWind.x*(.05+.07*windStrength)*sin(windTime*1.6+aWind.y*.7);
    transformed.z+=swing;transformed.x+=swing*.3;`,
};

export const windHook = (uniforms: WindUniforms, mode: WindMode): ShaderHook => ({
  key: `ship-wind-${mode}`,
  uniforms,
  vertexHeader: 'attribute vec4 aWind;uniform float windTime,windStrength,windGust,propAngle,sailSet;',
  vertex: [{ chunk: 'begin_vertex', after: windBodies[mode] }],
});

/** Gives a geometry its `aWind` from `fn(position, v, index, u)` (`airship-wind.js` `windAttribute`); missing components are 0. */
export const windAttribute = (geometry: THREE.BufferGeometry, fn: (p: THREE.Vector3, v: number, index: number, u: number) => readonly number[] | null): THREE.BufferGeometry => {
  const p = geometry.getAttribute('position');
  const uv = geometry.getAttribute('uv') as THREE.BufferAttribute | THREE.InterleavedBufferAttribute | undefined;
  const data = new Float32Array(p.count * 4);
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i += 1) {
    v.fromBufferAttribute(p, i);
    const out = fn(v, uv === undefined ? 0 : uv.getY(i), i, uv === undefined ? 0 : uv.getX(i)) ?? [];
    data.set([out[0] ?? 0, out[1] ?? 0, out[2] ?? 0, out[3] ?? 0], i * 4);
  }
  geometry.setAttribute('aWind', new THREE.BufferAttribute(data, 4));
  return geometry;
};

/** The roof reveal: `fade` 0 shows the part, 1 dissolves it (ordered dither; the shadow pass is unaffected). */
export const revealHook = (fade: THREE.IUniform<number>): ShaderHook => ({
  key: 'ship-reveal',
  uniforms: { revealFade: fade },
  fragmentHeader: 'uniform float revealFade;',
  fragment: [
    {
      chunk: 'alphatest_fragment',
      after: `if(revealFade>0.){
  mat4 revealBayer=mat4(0.,8.,2.,10.,12.,4.,14.,6.,3.,11.,1.,9.,15.,7.,13.,5.);
  float revealThreshold=(revealBayer[int(mod(gl_FragCoord.x,4.))][int(mod(gl_FragCoord.y,4.))]+.5)/16.;
  if(revealFade>revealThreshold)discard;
}`,
    },
  ],
});

/** A baked irradiance volume's uniforms: RGB the local bounce, alpha the daylight visibility (`createRadianceVolume`). */
export type RadianceUniforms = {
  readonly hdRadiance: THREE.IUniform<THREE.Data3DTexture | null>;
  readonly hdRadianceMin: THREE.IUniform<THREE.Vector3>;
  readonly hdRadianceSpan: THREE.IUniform<THREE.Vector3>;
};
export const createRadianceUniforms = (): RadianceUniforms => ({ hdRadiance: { value: null }, hdRadianceMin: { value: new THREE.Vector3() }, hdRadianceSpan: { value: new THREE.Vector3(1, 1, 1) } });

/** The shared engine's `installRadiance` (default slots): daylight scales the direct light, the bake adds bounce. */
export const radianceHook = (uniforms: RadianceUniforms): ShaderHook => ({
  key: 'ship-radiance',
  uniforms,
  vertexHeader: 'varying vec3 hdLightPosition;',
  fragmentHeader: 'uniform highp sampler3D hdRadiance;uniform vec3 hdRadianceMin,hdRadianceSpan;varying vec3 hdLightPosition;',
  vertex: [
    {
      chunk: 'worldpos_vertex',
      after: `vec4 hdLightVertex=vec4(transformed,1.);
#ifdef USE_INSTANCING
  hdLightVertex=instanceMatrix*hdLightVertex;
#endif
hdLightPosition=(modelMatrix*hdLightVertex).xyz;`,
    },
  ],
  fragment: [
    {
      chunk: 'lights_fragment_end',
      after: `vec3 lightUVW=(hdLightPosition-hdRadianceMin)/hdRadianceSpan;
float inVolume=step(0.,lightUVW.x)*step(lightUVW.x,1.)*step(0.,lightUVW.y)*step(lightUVW.y,1.)*step(0.,lightUVW.z)*step(lightUVW.z,1.);
vec4 bakedLight=vec4(0.,0.,0.,1.);
if(inVolume>.5) bakedLight=texture(hdRadiance,lightUVW);
float daylight=mix(1.,bakedLight.a,inVolume);
reflectedLight.directDiffuse*=daylight;
reflectedLight.indirectDiffuse*=mix(1.,.23,1.-daylight);
reflectedLight.directSpecular*=daylight;
reflectedLight.indirectDiffuse+=diffuseColor.rgb*bakedLight.rgb*3.*inVolume;`,
    },
  ],
});

/** The baked lamp light's uniforms (`airship-lamps.js`): the volume, its box, and the time of day's `lamps`. */
export type LampUniforms = {
  readonly lampVolume: THREE.IUniform<THREE.Data3DTexture | null>;
  readonly lampMin: THREE.IUniform<THREE.Vector3>;
  readonly lampSpan: THREE.IUniform<THREE.Vector3>;
  readonly lampLevel: THREE.IUniform<number>;
};
export const createLampUniforms = (): LampUniforms => ({ lampVolume: { value: null }, lampMin: { value: new THREE.Vector3() }, lampSpan: { value: new THREE.Vector3(1, 1, 1) }, lampLevel: { value: 0 } });

export const lampHook = (uniforms: LampUniforms): ShaderHook => ({
  key: 'ship-lamps',
  uniforms,
  vertexHeader: 'varying vec3 lampWorld;',
  fragmentHeader: 'uniform highp sampler3D lampVolume;uniform vec3 lampMin,lampSpan;uniform float lampLevel;varying vec3 lampWorld;',
  vertex: [{ chunk: 'worldpos_vertex', after: 'lampWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
  fragment: [
    {
      chunk: 'aomap_fragment',
      after: `if(lampLevel>.001){
  vec3 lampUvw=(lampWorld-lampMin)/lampSpan;
  if(all(greaterThanEqual(lampUvw,vec3(0.)))&&all(lessThanEqual(lampUvw,vec3(1.))))
    reflectedLight.indirectDiffuse+=diffuseColor.rgb*texture(lampVolume,lampUvw).rgb*2.1*lampLevel;
}`,
    },
  ],
});

/** The cloud shadows' uniforms (`airship-weather.js` `cloudShadowUniforms`): x strength, y coverage, z tiles per metre, w softness. */
export type CloudUniforms = {
  readonly csNoise: THREE.IUniform<THREE.Texture | null>;
  readonly csParams: THREE.IUniform<THREE.Vector4>;
  readonly csOffset: THREE.IUniform<THREE.Vector2>;
  readonly csShift: THREE.IUniform<THREE.Vector2>;
};
export const createCloudUniforms = (): CloudUniforms => ({ csNoise: { value: null }, csParams: { value: new THREE.Vector4(0, 0.6, 1 / 50, 0.05) }, csOffset: { value: new THREE.Vector2() }, csShift: { value: new THREE.Vector2() } });

export const cloudShadowHook = (uniforms: CloudUniforms): ShaderHook => ({
  key: 'ship-cloud-shadow',
  uniforms,
  vertexHeader: 'varying vec3 csWorld;',
  fragmentHeader: `uniform sampler2D csNoise;uniform vec4 csParams;uniform vec2 csOffset,csShift;varying vec3 csWorld;
float airshipCloudShadow(vec3 p){
  vec2 uv=(p.xz+p.y*csShift)*csParams.z+csOffset;
  float n=texture2D(csNoise,uv).r*.78+texture2D(csNoise,uv*1.7+vec2(.37,.11)).g*.22;
  return 1.-csParams.x*smoothstep(csParams.y-csParams.w,csParams.y+csParams.w,n);
}`,
  vertex: [{ chunk: 'worldpos_vertex', after: 'csWorld=(modelMatrix*vec4(transformed,1.)).xyz;' }],
  fragment: [
    {
      chunk: 'lights_fragment_end',
      after: `if(csParams.x>0.){float cloudSun=airshipCloudShadow(csWorld);
  reflectedLight.directDiffuse*=cloudSun;reflectedLight.directSpecular*=cloudSun;}`,
    },
  ],
});

/** The bridge console's phosphor: vertex colour times the emissive (`airship-cockpit.js`). */
export const crtHook: ShaderHook = {
  key: 'ship-crt',
  fragment: [{ chunk: 'emissivemap_fragment', after: '#ifdef USE_COLOR\n totalEmissiveRadiance *= vColor.rgb;\n#endif' }],
};
